/**
 * PHYSICAL-RISK.PROMPT
 * Un seul endroit decrit un risque a l'assistant : les prompts des attributs, des mesures de
 * prevention et du niveau de prevention partent tous de la meme description.
 */
import { PreventionAction } from "@/entities/prevention-action/model/prevention-action.types"
import { Population } from "@/entities/population/model/population.types"
import { RPS_RISK_AXES } from "@/entities/population/model/rps-risk"
import { PhysicalRisk } from "./physical-risk.types"
import { getRiskAttributeFields } from "./physical-risk.utils"

interface DescribeRiskOptions {
  //Attribut en cours de redaction : il n'a rien a apprendre a l'assistant sur lui-meme
  excludeAttributeId?: string
  //A taire quand c'est justement le niveau de prevention que l'on fait evaluer
  isPreventionHidden?: boolean
}

export function describeRisk(risk: PhysicalRisk, options: DescribeRiskOptions = {}) {
  //L'assistant raisonne en libelles : le prompt ne parle jamais des ids de stockage
  const attributes = getRiskAttributeFields(risk).filter((item) => item.id !== options.excludeAttributeId)

  const lines = [
    "Risque :",
    ...attributes.map((item) => `- ${item.name} : ${item.value || "Non renseigné"}`),
    `Famille : ${risk.cFamily?.name ?? "Non renseignée"}`,
    `Gravité : ${risk.cSeverityLabel || "Non renseignée"}`,
    `Fréquence : ${risk.cFrequencyLabel || "Non renseignée"}`
  ]

  if (!options.isPreventionHidden) {
    lines.push(`Prévention actuelle : ${risk.cPreventionLabel || "Non renseignée"}`)
  }

  return lines.join("\n")
}

/** Risques psychosociaux des fonctions d'une mesure RPS : leurs situations, axe par axe */
export function describeRps(populations: Population[]) {
  const situations = populations.flatMap((population) =>
    population.rpsRisks.flatMap((rpsRisk) => {
      const axis = RPS_RISK_AXES.find((item) => item.id === rpsRisk.axis)?.label ?? rpsRisk.axis
      return rpsRisk.situations.map((situation) => `- ${population.name} · ${axis} : ${situation}`)
    })
  )

  return [
    "Risque : risques psychosociaux",
    `Fonctions : ${populations.map((population) => population.name).join(", ") || "Non renseignées"}`,
    `Situations :\n${situations.join("\n") || "- Non renseignées"}`
  ].join("\n")
}

/** Liste de mesures pour un prompt. Le titre reste a l'appelant : il ne parle pas des memes mesures. */
export function describePreventionActions(title: string, actions: PreventionAction[]) {
  if (!actions.length) return `${title} :\n- Aucune`

  return `${title} :\n${actions.map(describePreventionAction).join("\n")}`
}

function describePreventionAction(action: PreventionAction) {
  const detail = action.description ? ` : ${action.description.replace(/\n/g, " ")}` : ""
  const periodic = action.refreshDelay ? " (mesure périodique)" : ""
  return `- ${action.name}${detail}${periodic}`
}
